import PropTypes from "prop-types"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { Flex } from "antd"
import { EditOutlined, MailOutlined } from "@ant-design/icons"
import { routes } from "router"
import { Phone } from "ui/Icon/DSIcon/Phone"
import IconWithBg from "ui/IconWithBg"
import ClientContact from "pagesV2/pro/clients/ClientContact"
import EditClientModal from "ui/Booking/Details/Body/ClientDetails/EditClientModal"
import AvatarPlaceholder from "../AvatarPlaceholder/AvatarPlaceholder"
import styles from "../../index.module.css"

const ClientDetailsCard = ({ client = {}, bookingUuid, bookingId, onRefresh }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isEditOpen, setIsEditOpen] = useState(false)

  // The client detail page hits /guide/clients/{id}, so it is keyed by the guide-client id
  // (same id the clients list uses) — NOT the messaging user_id. The reservation payload may
  // expose it as guide_client_id or as the client record `id`.
  const clientDetailId = client.guideClientId ?? client.id ?? null
  const goToClient = () => clientDetailId && navigate(routes.guideClientPath({ id: clientDetailId }))

  // ClientContact keys off user_id and reads email/phone from a nested `user` object.
  const clientForContact = useMemo(
    () => ({
      ...client,
      user: {
        first_name: client.first_name,
        last_name: client.last_name,
        phone_number: client.phone,
        email: client.email
      }
    }),
    [client]
  )

  return (
    <div className={styles.card}>
      <Flex align="center" justify="space-between" className={styles.cardHeader}>
        <span className={styles.cardTitle}>{t("booking.reservation_detail.client_details")}</span>
        <Flex align="center" gap={6}>
          <ClientContact client={clientForContact} bookingUuid={bookingUuid} bookingId={bookingId} />
          {/* Edit works off the guide-client id (not the user id) — hide it until the
              backend supplies guideClientId so the modal never opens empty. */}
          {client.guideClientId && <IconWithBg icon={<EditOutlined />} onClick={() => setIsEditOpen(true)} />}
        </Flex>
      </Flex>
      <Flex align="center" gap={12}>
        {client.avatarUrl ? (
          <img src={client.avatarUrl} alt={client.name} className={styles.avatar} />
        ) : (
          <AvatarPlaceholder name={client.name} />
        )}
        <Flex vertical gap={4}>
          {clientDetailId ? (
            <button type="button" className={styles.clientNameLink} onClick={goToClient}>
              {client.name}
            </button>
          ) : (
            <span className={styles.clientName}>{client.name}</span>
          )}
          {client.phone && (
            <Flex align="center" gap={6}>
              <Phone width={14} height={14} fill="var(--color-text-secondary-dark)" />
              <span className={styles.contactText}>{client.phone}</span>
            </Flex>
          )}
          {client.email && (
            <Flex align="center" gap={6}>
              <MailOutlined className={styles.contactIcon} />
              <span className={styles.contactText}>{client.email}</span>
            </Flex>
          )}
        </Flex>
      </Flex>

      {isEditOpen && (
        <EditClientModal
          clientId={client.guideClientId}
          open={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          onSuccess={onRefresh}
        />
      )}
    </div>
  )
}

ClientDetailsCard.propTypes = {
  client: PropTypes.object,
  bookingUuid: PropTypes.string,
  bookingId: PropTypes.number,
  onRefresh: PropTypes.func
}

export default ClientDetailsCard
